// AdminPlatformDashboard.tsx — Phase 8 platform-wide admin view.
//
// Five panels:
//   1. Platform True Net P&L (per-pillar admin share; offers cost; net)
//   2. Per-agent earnings table
//   3. All-deposits status board (red rows for breach/suspend)
//   4. Settlement worker health + DLQ depth (from existing Phase 3 endpoints)
//   5. Resolution Panel modal — invoked from a defaulted/suspended agent row.
//      Two actions: Transfer downline / Lock. (Phase 9 eradicated the
//      "Override + Takeover" action.)
//
// Phase 2 UI overhaul (2026-05-15): visual surfaces realigned with the
// player/agent arena-tinted glassmorphic look — hard borders → soft
// arena-tinted insetting rings, neutral palette → arena/neon tokens,
// fuchsia window pill → violet (admin identity), dense table rows →
// spaced-out hover-highlight rows. Modal chrome adopts the unified
// arena ring + backdrop-blur. Data + API calls unchanged.

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { levelName } from '../../lib/levelNames';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { INPUT_CLS } from '../../components/fieldStyles';

type WindowKey = 'all' | '24h' | '7d' | '30d';

interface PlatformPnl {
  window: WindowKey;
  byPillar: Record<string, { total_base: number; agent_share: number; admin_share: number }>;
  offers: { platform_funded: number; split_platform_share: number; split_agent_share: number; total_cost_to_platform: number };
  platformTrueNet: number;
  perAgent: Array<{ id: number; username: string; level: number; suspended: boolean; wallet_balance: number; true_net_window: number }>;
}
interface DepositRow {
  id: number; username: string; level: number; parent_agent_id: number | null;
  status: 'healthy' | 'caution' | 'warning' | 'breach' | 'suspended' | 'locked';
  account_status: string; suspended_at: string | null; lock_reason: string | null;
  balance: number; deposit_balance: number; deposit_target: number;
  negative_exposure: number; ratio_pct: number; player_count: number;
}
interface SettlementHealth {
  queueDepth: number; processingDepth: number; failedDepth: number;
  dlqDepth: number; done24h: number; oldestQueuedAgeSec: number; healthy: boolean;
}
// Phase C (2026-09-06) — bg-tables-worker, the process that opens, decides and settles every
// communal round for Aviator, Andar Bahar and Teen Patti. GET /admin/worker/health/tables.
// It is its OWN bar rather than a column in the settlement one because they fail differently:
// the settlement worker backing up delays the agent chain, while this one stalling freezes
// tables players are sitting at. `status` is the endpoint's own verdict against its thresholds.
interface TablesHealth {
  worker: string;
  status: 'ok' | 'stale' | 'down' | 'degraded' | string;
  thresholds: { stale_after_seconds: number; down_after_seconds: number; round_overdue_seconds: number; max_settle_attempts: number };
  instances: Array<{
    worker_id: string; game: string; status: string;
    last_tick_at: string | null; staleness_seconds: number;
    totals: { ticks: number; processed: number; failed: number; dlq: number };
  }>;
  rounds: { live: number; overdue: number; by_game: Record<string, { live: number; overdue: number; by_status: Record<string, number> }> };
  backlog: { unpaid_bets: number; errored_bets: number; dlq_bets: number };
}
const GAME_LABEL: Record<string, string> = {
  aviator: 'Aviator', andar_bahar: 'Andar Bahar', teen_patti: 'Teen Patti', roulette: 'Roulette',
};

const PILLAR_LABEL: Record<string, string> = {
  platform_fee: 'Platform Fee',
  win_loss:     'Win/Loss Profit',
  offer_reward: 'Offer Reward',
};
const WINDOWS: Array<{ k: WindowKey; label: string }> = [
  { k: '24h', label: '24h' }, { k: '7d', label: '7d' }, { k: '30d', label: '30d' }, { k: 'all', label: 'All' },
];

function fmt(n: number) {
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  return `${sign}₹${Math.abs(n).toLocaleString()}`;
}
// Deposit row tone — semantic colours by status. Switched from
// `border` + `bg-*-50` to `ring-1 ring-inset` + softer arena base
// so the cards visually sit on the new glassmorphic surface.
function depositTone(s: DepositRow['status']) {
  switch (s) {
    case 'suspended':
    case 'breach':  return 'bg-red-50/80 dark:bg-red-950/30 ring-red-300/60 dark:ring-red-800/50';
    case 'locked':  return 'bg-rose-50/80 dark:bg-rose-950/30 ring-rose-300/60 dark:ring-rose-800/50';
    case 'warning': return 'bg-amber-50/80 dark:bg-amber-950/30 ring-amber-300/60 dark:ring-amber-800/50';
    case 'caution': return 'bg-yellow-50/80 dark:bg-yellow-950/20 ring-yellow-300/60 dark:ring-yellow-800/50';
    default:        return 'bg-white dark:bg-arena-700/40 ring-arena-200/50 dark:ring-arena-600/40';
  }
}

export default function PlatformPanel() {
  const [windowK, setWindowK] = useState<WindowKey>('7d');
  const [pnl, setPnl] = useState<PlatformPnl | null>(null);
  const [deposits, setDeposits] = useState<DepositRow[]>([]);
  const [health, setHealth] = useState<SettlementHealth | null>(null);
  const [tables, setTables] = useState<TablesHealth | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [resolutionAgent, setResolutionAgent] = useState<DepositRow | null>(null);

  const reload = useCallback(async () => {
    try {
      const [p, d, h] = await Promise.all([
        adminApi.get<PlatformPnl>(`/admin/dashboard/platform-pnl?window=${windowK}`),
        adminApi.get<{ rows: DepositRow[] }>(`/admin/dashboard/deposits`),
        adminApi.get<SettlementHealth>(`/admin/settlement/health`),
      ]);
      setPnl(p);
      setDeposits(d.rows);
      setHealth(h);
      setErr(null);
    } catch (e: any) { setErr(e.message); }
    // Fetched SEPARATELY and swallowed on failure, on purpose: this endpoint is newer than the
    // three above, and a backend that predates it (or a worker that has never run) must not blank
    // out the whole platform dashboard. No bar simply means "nothing to report".
    try {
      setTables(await adminApi.get<TablesHealth>(`/admin/worker/health/tables`));
    } catch { setTables(null); }
  }, [windowK]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {err && (
          <div className="px-4 py-3 rounded-2xl text-[13px]
                          bg-rose-50 dark:bg-rose-500/10
                          ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30
                          text-rose-700 dark:text-rose-200">{err}</div>
        )}

        {/* Worker health bar — surfaced prominently because a backed-up queue is critical */}
        {health && (
          <div className={`rounded-2xl ring-1 ring-inset px-4 py-3 flex items-center justify-between text-[12px]
                           backdrop-blur shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none ${
            health.healthy
              ? 'bg-emerald-50/80 dark:bg-emerald-950/30 ring-emerald-300/60 dark:ring-emerald-800/50'
              : 'bg-amber-50/80 dark:bg-amber-950/30 ring-amber-400/60 dark:ring-amber-700/60'
          }`}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-bold uppercase tracking-wider text-arena-900 dark:text-white">Settlement worker</span>
              <span className={health.healthy ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}>
                {health.healthy ? '✓ healthy' : '⚠ degraded'}
              </span>
              <span className="text-arena-600 dark:text-neon-200/60">queue: <span className="font-bold text-arena-900 dark:text-white">{health.queueDepth}</span></span>
              <span className="text-arena-600 dark:text-neon-200/60">processing: <span className="font-bold text-arena-900 dark:text-white">{health.processingDepth}</span></span>
              <span className="text-arena-600 dark:text-neon-200/60">DLQ: <span className={`font-bold ${health.dlqDepth > 0 ? 'text-red-600 dark:text-red-400' : 'text-arena-900 dark:text-white'}`}>{health.dlqDepth}</span></span>
              <span className="text-arena-600 dark:text-neon-200/60">24h done: <span className="font-bold text-arena-900 dark:text-white">{health.done24h}</span></span>
              {health.oldestQueuedAgeSec > 0 && <span className="text-arena-600 dark:text-neon-200/60">oldest queued: {health.oldestQueuedAgeSec}s</span>}
            </div>
          </div>
        )}

        {/* Table games worker (Phase C) — the same bar language as the settlement worker above, so
            an operator reads both the same way. Anything that needs a person is coloured: a stalled
            tick, an overdue round, a bet that never got paid. */}
        {tables && (() => {
          const stalled = tables.instances.filter(i => i.status !== 'ok');
          const stuck   = tables.backlog.unpaid_bets + tables.backlog.errored_bets + tables.backlog.dlq_bets;
          const wellSpecific = tables.status === 'ok' && stalled.length === 0 && tables.rounds.overdue === 0 && stuck === 0;
          return (
            <div className={`rounded-2xl ring-1 ring-inset px-4 py-3 text-[12px]
                             backdrop-blur shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none ${
              wellSpecific
                ? 'bg-emerald-50/80 dark:bg-emerald-950/30 ring-emerald-300/60 dark:ring-emerald-800/50'
                : 'bg-amber-50/80 dark:bg-amber-950/30 ring-amber-400/60 dark:ring-amber-700/60'
            }`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-bold uppercase tracking-wider text-arena-900 dark:text-white">Table games worker</span>
                <span className={wellSpecific ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}>
                  {wellSpecific ? '✓ healthy' : '⚠ needs a look'}
                </span>
                <span className="text-arena-600 dark:text-neon-200/60">live rounds: <span className="font-bold text-arena-900 dark:text-white">{tables.rounds.live}</span></span>
                <span className="text-arena-600 dark:text-neon-200/60">overdue: <span className={`font-bold ${tables.rounds.overdue > 0 ? 'text-red-600 dark:text-red-400' : 'text-arena-900 dark:text-white'}`}>{tables.rounds.overdue}</span></span>
                <span className="text-arena-600 dark:text-neon-200/60">unpaid bets: <span className={`font-bold ${tables.backlog.unpaid_bets > 0 ? 'text-red-600 dark:text-red-400' : 'text-arena-900 dark:text-white'}`}>{tables.backlog.unpaid_bets}</span></span>
                {tables.backlog.errored_bets > 0 && <span className="text-arena-600 dark:text-neon-200/60">errored: <span className="font-bold text-red-600 dark:text-red-400">{tables.backlog.errored_bets}</span></span>}
                {tables.backlog.dlq_bets > 0 && <span className="text-arena-600 dark:text-neon-200/60">DLQ: <span className="font-bold text-red-600 dark:text-red-400">{tables.backlog.dlq_bets}</span></span>}
              </div>
              {/* One chip per game it drives. A game with no chip is a game this worker is not
                  driving at all — which is the loudest thing this bar can say. */}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {tables.instances.length === 0 && (
                  <span className="text-amber-700 dark:text-amber-300">no game is being driven — the worker is not running</span>
                )}
                {tables.instances.map(i => {
                  const r = tables.rounds.by_game[i.game];
                  const good = i.status === 'ok' && (r?.overdue ?? 0) === 0;
                  return (
                    <span key={i.worker_id}
                      className={`px-2.5 py-1 rounded-full ring-1 ring-inset text-[11px] ${
                        good
                          ? 'bg-white/70 dark:bg-white/5 ring-arena-200/60 dark:ring-white/10 text-arena-700 dark:text-neon-200/70'
                          : 'bg-amber-100/80 dark:bg-amber-500/10 ring-amber-400/60 dark:ring-amber-600/50 text-amber-800 dark:text-amber-200'
                      }`}>
                      <span className="font-semibold text-arena-900 dark:text-white">{GAME_LABEL[i.game] || i.game}</span>
                      {' · '}{r?.live ?? 0} live
                      {(r?.overdue ?? 0) > 0 && <span className="font-bold text-red-600 dark:text-red-400"> · {r.overdue} overdue</span>}
                      {i.status !== 'ok' && <span className="font-bold"> · {i.status} {i.staleness_seconds}s</span>}
                      {i.totals.dlq > 0 && <span className="font-bold text-red-600 dark:text-red-400"> · {i.totals.dlq} dlq</span>}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Window selector — sticky for fast comparison while scrolling */}
        <div className="sticky top-0 z-10 -mx-2 px-2 sm:-mx-3 sm:px-3 lg:-mx-4 lg:px-4 py-2
                        backdrop-blur-md
                        bg-white/70 dark:bg-arena-900/60
                        flex items-center gap-2 flex-wrap">
          <span className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">Window</span>
          {WINDOWS.map(w => (
            <button key={w.k} onClick={() => setWindowK(w.k)}
              className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition ring-1 ring-inset ${
                windowK === w.k
                  ? 'bg-violet-600 text-white ring-violet-600 shadow-[0_2px_10px_rgba(139,92,246,0.4)]'
                  : 'bg-white text-arena-700 ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/80 dark:ring-arena-600/40 dark:hover:bg-arena-700/60'
              }`}>
              {w.label}
            </button>
          ))}
        </div>

        {/* UX Phase 3: one-tap jump from the profit view to the full Game Money
            hub (daily report, simulator, earnings sliders, economics). */}
        <Link
          to="/game-money?tab=profit"
          className="flex items-center gap-2.5 rounded-xl mb-3 px-3.5 py-2.5
                     bg-emerald-500/10 hover:bg-emerald-500/15 transition
                     ring-1 ring-inset ring-emerald-400/30 text-emerald-800 dark:text-emerald-200"
        >
          <Icon name="payments" size={18} filled className="text-emerald-600 dark:text-emerald-300" />
          <span className="flex-1 text-[13px] font-bold">Open Game Money — report, simulator &amp; earnings</span>
          <Icon name="chevron_right" size={18} />
        </Link>

        {/* Platform True Net */}
        {pnl && (
          <div className="rounded-2xl
                          bg-white dark:bg-arena-700/40
                          ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                          shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                          backdrop-blur p-5">
            <div className="flex items-baseline gap-3 flex-wrap mb-4">
              <span className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">Platform True Net</span>
              <span className={`text-3xl font-display font-black tabular-nums ${pnl.platformTrueNet >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{fmt(pnl.platformTrueNet)}</span>
              <span className="text-[11px] text-arena-600 dark:text-neon-200/60">= admin fee share + admin win/loss share − offer costs</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {Object.entries(pnl.byPillar).map(([pillar, v]) => (
                <div key={pillar}
                     className="rounded-xl
                                bg-arena-50/40 dark:bg-arena-800/40
                                ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 px-3 py-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">{PILLAR_LABEL[pillar] || pillar}</div>
                  <div className="grid grid-cols-3 gap-2 mt-1.5 text-[11px]">
                    <div><div className="text-arena-500 dark:text-neon-200/50">Total base</div><div className="font-bold text-arena-900 dark:text-white tabular-nums">{fmt(v.total_base)}</div></div>
                    <div><div className="text-arena-500 dark:text-neon-200/50">Agents</div><div className="font-bold text-arena-900 dark:text-white tabular-nums">{fmt(v.agent_share)}</div></div>
                    <div><div className="text-arena-500 dark:text-neon-200/50">Admin</div><div className={`font-bold tabular-nums ${v.admin_share >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{fmt(v.admin_share)}</div></div>
                  </div>
                </div>
              ))}
            </div>
            {pnl.offers.total_cost_to_platform > 0 && (
              // 2026-05-16 color/sign audit: offer costs are POSITIVE
              // amounts in the API (representing cost magnitude) but
              // they are LOSSES from the platform's POV. Show them
              // with a minus sign and red color so sign + color both
              // speak "cost". Agent co-funding is platform-neutral
              // (it's the agents' contribution, not the platform's
              // outflow) so it stays plain.
              <div className="mt-3 text-[11px] text-arena-600 dark:text-neon-200/60">
                Offer costs: platform-funded{' '}
                <span className="font-bold text-red-600 dark:text-red-400">{fmt(-pnl.offers.platform_funded)}</span>{' '}
                + split-platform{' '}
                <span className="font-bold text-red-600 dark:text-red-400">{fmt(-pnl.offers.split_platform_share)}</span>{' = '}
                <span className="font-bold text-red-600 dark:text-red-400">{fmt(-pnl.offers.total_cost_to_platform)}</span>{' '}
                total to platform
                {pnl.offers.split_agent_share > 0 && (
                  <span> (+ ₹{pnl.offers.split_agent_share.toLocaleString()} co-funded by agents)</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Per-agent earnings */}
        {pnl && pnl.perAgent.length > 0 && (
          <div className="rounded-2xl overflow-hidden
                          bg-white dark:bg-arena-700/40
                          ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                          shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                          backdrop-blur">
            <div className="px-4 py-3 text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">
              Per-agent earnings ({windowK})
            </div>
            <table className="w-full text-[12.5px]">
              <thead className="bg-arena-50/60 dark:bg-arena-800/40
                                text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60
                                border-y border-arena-200/50 dark:border-arena-600/40">
                <tr>
                  <th className="text-left  py-2.5 px-4">Agent</th>
                  <th className="text-right py-2.5 px-4">Wallet</th>
                  <th className="text-right py-2.5 px-4">True Net</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-arena-200/40 dark:divide-arena-700/40">
                {pnl.perAgent.map(a => (
                  <tr key={a.id} className="hover:bg-arena-50/60 dark:hover:bg-white/[0.03] transition">
                    <td className="py-3 px-4">
                      <span className="font-medium text-arena-900 dark:text-white">@{a.username}</span>
                      <span className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold ml-1.5">{levelName(a.level)}</span>
                      {a.suspended && (
                        <span className="ml-2 px-1.5 py-0.5 rounded
                                         bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300
                                         ring-1 ring-red-200 dark:ring-red-700/60
                                         text-[10px] uppercase tracking-wider font-bold">suspended</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right font-mono tabular-nums text-arena-700 dark:text-neon-200/80">{fmt(a.wallet_balance)}</td>
                    <td className={`py-3 px-4 text-right font-bold tabular-nums ${a.true_net_window >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{fmt(a.true_net_window)}</td>
                    <td className="py-3 px-4 text-right text-arena-400 dark:text-neon-200/40">
                      <a href={`/agents/${a.id}`} className="hover:text-violet-600 dark:hover:text-violet-300 transition">
                        <Icon name="open_in_new" size={14} />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Deposits status board */}
        <div className="rounded-2xl
                        bg-white dark:bg-arena-700/40
                        ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                        shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                        backdrop-blur p-5">
          <div className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold mb-3">Deposits status board</div>
          <div className="space-y-2.5">
            {deposits.map(d => (
              <div key={d.id} className={`rounded-xl ring-1 ring-inset px-4 py-3 ${depositTone(d.status)}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-bold text-arena-900 dark:text-white">@{d.username}</span>
                  <span className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">{levelName(d.level)}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                    d.status === 'suspended' || d.status === 'breach' ? 'bg-red-200 dark:bg-red-900/60 text-red-900 dark:text-red-200'
                    : d.status === 'locked' ? 'bg-rose-200 dark:bg-rose-900/60 text-rose-900 dark:text-rose-200'
                    : d.status === 'warning' ? 'bg-amber-200 dark:bg-amber-900/60 text-amber-900 dark:text-amber-200'
                    : d.status === 'caution' ? 'bg-yellow-200 dark:bg-yellow-900/60 text-yellow-900 dark:text-yellow-200'
                    : 'bg-emerald-200 dark:bg-emerald-900/60 text-emerald-900 dark:text-emerald-200'
                  }`}>{d.status}</span>
                  <span className="text-[11.5px] text-arena-700 dark:text-neon-200/75">
                    balance {fmt(d.balance)} · target ₹{d.deposit_target.toLocaleString()} · ratio {d.ratio_pct.toFixed(1)}% · {d.player_count} player{d.player_count === 1 ? '' : 's'}
                  </span>
                  {(d.status === 'suspended' || d.status === 'breach' || d.status === 'locked') && (
                    <button onClick={() => setResolutionAgent(d)}
                      className="ml-auto px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider
                                 bg-violet-600 hover:bg-violet-500 text-white
                                 shadow-[0_2px_10px_rgba(139,92,246,0.4)] transition active:scale-95">
                      Resolution panel →
                    </button>
                  )}
                </div>
                {d.lock_reason && <div className="text-[10.5px] text-arena-600 dark:text-neon-200/60 mt-1.5 font-mono">{d.lock_reason}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {resolutionAgent && (
        <ResolutionPanelModal agent={resolutionAgent} allAgents={deposits} onClose={() => setResolutionAgent(null)} onDone={() => { setResolutionAgent(null); reload(); }} />
      )}
    </div>
  );
}

// ─── Admin Resolution Panel — the core Phase 8 deliverable ──────────

function ResolutionPanelModal({ agent, allAgents, onClose, onDone }: {
  agent: DepositRow; allAgents: DepositRow[]; onClose: () => void; onDone: () => void;
}) {
  // Phase 9 (2026-05-11): the 'takeover' action was eradicated. Locked
  // agents keep their own QR — their downline still pays them and
  // their queue still fills. The agent logs in via the restricted
  // gate to action it. Admin's only resolution levers now are
  // Transfer + Lock.
  type Action = null | 'transfer' | 'lock';
  const [action, setAction] = useState<Action>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Transfer form state
  const [targetId, setTargetId] = useState<number | 'admin_direct'>('admin_direct');
  const [transferReason, setTransferReason] = useState('');

  // Lock form state
  const [lockReason, setLockReason] = useState('');

  const submit = async () => {
    setErr(null); setBusy(true);
    try {
      if (action === 'transfer') {
        await adminApi.post(`/admin/dashboard/agents/${agent.id}/resolution/transfer`, {
          target_agent_id: targetId === 'admin_direct' ? null : Number(targetId),
          reason: transferReason || undefined,
        });
      } else if (action === 'lock') {
        if (!lockReason.trim()) { setErr('Reason required'); setBusy(false); return; }
        await adminApi.post(`/admin/dashboard/agents/${agent.id}/resolution/lock`, { reason: lockReason });
      }
      onDone();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // Eligible transfer targets: any non-suspended, non-locked agent OTHER than this one.
  const eligibleTargets = allAgents.filter(a => a.id !== agent.id && a.status !== 'suspended' && a.status !== 'locked');

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="rounded-2xl shadow-2xl max-w-xl w-full p-5 space-y-4
                      bg-white dark:bg-arena-800
                      ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-base font-bold text-arena-900 dark:text-white">Resolution Panel — @{agent.username}</div>
            <div className="text-[11px] text-arena-600 dark:text-neon-200/60">{levelName(agent.level)} · status <span className="font-bold uppercase">{agent.status}</span> · {agent.player_count} direct player{agent.player_count === 1 ? '' : 's'}</div>
          </div>
          <button onClick={onClose} className="text-2xl leading-none text-arena-400 dark:text-neon-200/70 hover:text-arena-700 dark:hover:text-white transition">×</button>
        </div>

        {err && (
          <div className="px-3 py-2 rounded-xl text-[12px]
                          bg-rose-50 dark:bg-rose-500/10
                          ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30
                          text-rose-700 dark:text-rose-200">{err}</div>
        )}

        {!action && (
          <div className="space-y-2.5">
            <div className="text-xs text-arena-700 dark:text-neon-200/75 mb-3">Choose an action to resolve this defaulted agent:</div>
            <button onClick={() => setAction('transfer')}
              className="w-full text-left rounded-xl p-3.5
                         ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                         hover:bg-cyan-50/60 dark:hover:bg-cyan-950/30
                         hover:ring-cyan-300/60 dark:hover:ring-cyan-700/60 transition">
              <div className="text-sm font-bold text-cyan-700 dark:text-cyan-400">Transfer downline</div>
              <div className="text-[11.5px] text-arena-600 dark:text-neon-200/60 mt-0.5 leading-relaxed">Move this agent's direct players + child agents to another agent (or to admin direct). Clears the suspension. Downline experience uninterrupted.</div>
            </button>
            <button onClick={() => setAction('lock')}
              className="w-full text-left rounded-xl p-3.5
                         ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                         hover:bg-red-50/60 dark:hover:bg-red-950/30
                         hover:ring-red-300/60 dark:hover:ring-red-700/60 transition">
              <div className="text-sm font-bold text-red-700 dark:text-red-400">Lock & freeze</div>
              <div className="text-[11.5px] text-arena-600 dark:text-neon-200/60 mt-0.5 leading-relaxed">Hard-lock the agent. They can still log in to a restricted view to action their pending downline requests + submit emergency recharges to clear the lockout. Their downline still sees their own QR and queues new requests at them.</div>
            </button>
            {/* Phase 9 (2026-05-11): the "Override + takeover" action
                was eradicated. Defaulter agents are no longer hijacked
                onto admin's QR — they keep their own QR even when
                suspended and use the restricted gate to bail
                themselves out by actioning their inbox. */}
          </div>
        )}

        {action === 'transfer' && (
          <div className="space-y-3">
            <div className="text-xs font-bold text-cyan-700 dark:text-cyan-400 uppercase tracking-wider">Transfer downline</div>
            <label className="block text-xs">
              <div className="text-arena-600 dark:text-neon-200/60 mb-1">Target</div>
              <select value={targetId === 'admin_direct' ? 'admin_direct' : String(targetId)}
                      onChange={e => setTargetId(e.target.value === 'admin_direct' ? 'admin_direct' : Number(e.target.value))}
                      className={INPUT_CLS}>
                <option value="admin_direct">— Admin direct (no agent) —</option>
                {eligibleTargets.map(a => (<option key={a.id} value={a.id}>@{a.username} ({levelName(a.level)})</option>))}
              </select>
            </label>
            <label className="block text-xs">
              <div className="text-arena-600 dark:text-neon-200/60 mb-1">Reason (optional)</div>
              <input type="text" value={transferReason} onChange={e => setTransferReason(e.target.value)} className={INPUT_CLS} />
            </label>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button onClick={() => setAction(null)} className="px-3 py-2 text-xs uppercase tracking-wider text-arena-700 dark:text-neon-200/80 hover:underline">Back</button>
              <button onClick={submit} disabled={busy}
                className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider
                           bg-cyan-600 text-white hover:bg-cyan-500
                           shadow-[0_2px_10px_rgba(8,145,178,0.4)]
                           transition active:scale-95 disabled:opacity-50">
                {busy ? 'Transferring…' : 'Confirm transfer'}
              </button>
            </div>
          </div>
        )}

        {action === 'lock' && (
          <div className="space-y-3">
            <div className="text-xs font-bold text-red-700 dark:text-red-400 uppercase tracking-wider">Lock & freeze</div>
            <div className="text-[11.5px] text-arena-600 dark:text-neon-200/60 leading-relaxed">Sets agents.status='suspended'. The agent cannot log in. Their downline players cannot place new bets (chain-suspension gate is already active).</div>
            <label className="block text-xs">
              <div className="text-arena-600 dark:text-neon-200/60 mb-1">Reason (required) <span className="text-red-500">*</span></div>
              <input type="text" value={lockReason} onChange={e => setLockReason(e.target.value)} placeholder="e.g. unpaid balance > 30 days" className={INPUT_CLS} />
            </label>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button onClick={() => setAction(null)} className="px-3 py-2 text-xs uppercase tracking-wider text-arena-700 dark:text-neon-200/80 hover:underline">Back</button>
              <button onClick={submit} disabled={busy || !lockReason.trim()}
                className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider
                           bg-red-600 text-white hover:bg-red-500
                           shadow-[0_2px_10px_rgba(239,68,68,0.4)]
                           transition active:scale-95 disabled:opacity-50">
                {busy ? 'Locking…' : 'Confirm lock'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
